/**
 * Evens out the loudness of your microphone before it's sent: quiet voices (a phone held
 * away from the face, a soft-spoken person, phones whose built-in gain control is weak) come
 * through louder, loud ones aren't pushed further, and peaks never clip.
 *
 * Measured with a steady tone (RMS in, RMS out): -50 dBFS → -35, -40 → -25, -30 → -15,
 * -20 → -10, -12 → -7, -6 → -6, 0 → -4 (peaks at most about -1 dBFS).
 */
import type { AudioProcessorOptions, Track, TrackProcessor } from 'livekit-client';

export const LEVELER = {
  compressor: { threshold: -35, knee: 20, ratio: 4, attack: 0.005, release: 0.25 },
  makeupDb: 3,
  limiter: { threshold: -3, knee: 0, ratio: 20, attack: 0.001, release: 0.1 },
};

type Settings = typeof LEVELER.compressor;

function compressor(c: BaseAudioContext, s: Settings) {
  const n = c.createDynamicsCompressor();
  n.threshold.value = s.threshold;
  n.knee.value = s.knee;
  n.ratio.value = s.ratio;
  n.attack.value = s.attack;
  n.release.value = s.release;
  return n;
}

/** Connect the leveler between `source` and `destination`; returns its nodes. */
export function buildLeveler(c: BaseAudioContext, source: AudioNode, destination: AudioNode) {
  const comp = compressor(c, LEVELER.compressor);
  const makeup = c.createGain();
  makeup.gain.value = 10 ** (LEVELER.makeupDb / 20);
  const limiter = compressor(c, LEVELER.limiter);
  source.connect(comp).connect(makeup).connect(limiter).connect(destination);
  return [comp, makeup, limiter];
}

const peak = (an: AnalyserNode, buf: Float32Array<ArrayBuffer>) => {
  an.getFloatTimeDomainData(buf);
  let p = 0;
  for (const x of buf) p = Math.max(p, Math.abs(x));
  return p;
};

export type VoiceLeveler = TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> & {
  /**
   * Watch input and output for a while: true if the microphone has sound but the leveler
   * puts out silence (some phones' audio processing does that), so it should be turned off.
   */
  silencesMicrophone(ms?: number): Promise<boolean>;
};

export function createVoiceLeveler(): VoiceLeveler {
  let nodes: AudioNode[] = [];
  let probes: [AnalyserNode, AnalyserNode] | null = null;
  const processor: VoiceLeveler = {
    name: 'voice-leveler',
    async init(opts) {
      const c = opts.audioContext;
      const source = c.createMediaStreamSource(new MediaStream([opts.track]));
      const out = c.createMediaStreamDestination();
      const leveler = buildLeveler(c, source, out);
      const inProbe = c.createAnalyser();
      const outProbe = c.createAnalyser();
      source.connect(inProbe);
      leveler.at(-1)!.connect(outProbe);
      probes = [inProbe, outProbe];
      nodes = [source, ...leveler, out, inProbe, outProbe];
      processor.processedTrack = out.stream.getAudioTracks()[0];
    },
    async silencesMicrophone(ms = 3000) {
      if (!probes) return false;
      const [inProbe, outProbe] = probes;
      const buf = new Float32Array(inProbe.fftSize);
      let heard = 0;
      let sent = 0;
      for (let t = 0; t < ms; t += 100) {
        await new Promise((r) => setTimeout(r, 100));
        heard = Math.max(heard, peak(inProbe, buf));
        sent = Math.max(sent, peak(outProbe, buf));
      }
      return heard > 0.001 && sent < 0.0001;
    },
    async restart(opts) {
      await processor.destroy();
      await processor.init(opts);
    },
    async destroy() {
      for (const n of nodes) n.disconnect();
      nodes = [];
      probes = null;
      processor.processedTrack?.stop();
      processor.processedTrack = undefined;
    },
  };
  return processor;
}
